import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  ChevronDown,
  ChevronRight,
  CirclePlus,
  Link2,
  Link2Off,
  PencilLine,
  Trash2,
  UserRoundMinus,
  UserRoundPlus,
} from 'lucide-react';

import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Skeleton } from '@/shared/ui';

import { useElementHistory, useElementHistoryRun } from '../queries/useElement';
import { accountStateClass, accountStateKey } from '../model/accountState';
import type { ElementEventKind, ElementHistoryEvent } from '../model/types';
import styles from './ElementHistory.module.css';

/** Ikona na oś czasu. ⚠ Kształt niesie RODZAJ zmiany; kolor zostaje zarezerwowany (dec. 58). */
const ICONS: Record<ElementEventKind, typeof CirclePlus> = {
  created: CirclePlus,
  fieldChanged: PencilLine,
  deleted: Trash2,
  relationAdded: Link2,
  relationRemoved: Link2Off,
  ownerAdded: UserRoundPlus,
  ownerRemoved: UserRoundMinus,
};

interface Props {
  elementId: number;
}

/**
 * Historia elementu (decyzja 104, wariant H2).
 *
 * ⭐⭐ **Przebieg importu jest JEDNĄ pozycją, zwiniętą.** To nie jest oszczędność miejsca:
 * element tknięty trzema przebiegami ma siedemnaście ogniw importowych na dwadzieścia trzy,
 * więc rozwinięta oś czasu opowiada o maszynie — a pytanie, dla którego historia istnieje,
 * brzmi „**kto to zmienił i dlaczego**". Zwinięcie przywraca człowieka na wierzch, a przełącznik
 * „tylko ludzie" pozwala zdjąć maszynę zupełnie.
 *
 * ⛔ Zwijania **nie robi front** — robi je serwer. Inaczej trzeba by ściągnąć wszystkie ogniwa,
 * żeby pokazać sześć pozycji, i sufit odczytu wróciłby tylnymi drzwiami.
 */
export function ElementHistory({ elementId }: Props) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();

  const [open, setOpen] = useState(false);
  const [humanOnly, setHumanOnly] = useState(false);

  const history = useElementHistory(elementId, humanOnly, open);

  if (!open) {
    return (
      <Button variant="ghost" onClick={() => setOpen(true)}>
        {t('graph.history.show')}
      </Button>
    );
  }

  const events = history.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div className={styles['history']}>
      <div className={styles['toolbar']}>
        <fieldset className={styles['filter']}>
          <legend className={styles['srOnly']}>{t('graph.history.filter')}</legend>

          <button
            type="button"
            className={styles['filterButton']}
            aria-pressed={!humanOnly}
            onClick={() => setHumanOnly(false)}
          >
            {t('graph.history.everything')}
          </button>

          <button
            type="button"
            className={styles['filterButton']}
            aria-pressed={humanOnly}
            onClick={() => setHumanOnly(true)}
          >
            {t('graph.history.peopleOnly')}
          </button>
        </fieldset>

        <Button variant="ghost" onClick={() => setOpen(false)}>
          {t('graph.history.hide')}
        </Button>
      </div>

      {history.isPending ? (
        <div aria-busy="true" className={styles['loading']}>
          <Skeleton height="2.5rem" announce />
          <Skeleton height="2.5rem" />
          <Skeleton height="2.5rem" />
        </div>
      ) : history.isError ? (
        <div className={styles['failure']} role="alert">
          <p>{describeError(history.error)}</p>
          <Button onClick={() => void history.refetch()}>{t('common.action.retry')}</Button>
        </div>
      ) : events.length === 0 ? (
        <p className={styles['empty']}>
          {humanOnly ? t('graph.history.emptyPeople') : t('graph.history.empty')}
        </p>
      ) : (
        <>
          <ol className={styles['timeline']}>
            {events.map((event) => (
              <EventRow key={event.id} elementId={elementId} event={event} />
            ))}
          </ol>

          {history.hasNextPage ? (
            <div className={styles['more']}>
              <Button
                variant="ghost"
                disabled={history.isFetchingNextPage}
                onClick={() => void history.fetchNextPage()}
              >
                {history.isFetchingNextPage
                  ? t('graph.history.loadingOlder')
                  : t('graph.history.older')}
              </Button>
            </div>
          ) : (
            <p className={styles['end']}>{t('graph.history.end')}</p>
          )}
        </>
      )}
    </div>
  );
}

/** Zdarzenie: pojedyncze ogniwo albo zwinięty przebieg z własnym rozwinięciem. */
function EventRow({ elementId, event }: { elementId: number; event: ElementHistoryEvent }) {
  const collapsed = event.importRunId !== null && event.linkCount > 1;

  return collapsed ? (
    <ImportRow elementId={elementId} event={event} />
  ) : (
    <li className={styles['row']}>
      <SingleEvent event={event} />
    </li>
  );
}

function ImportRow({ elementId, event }: { elementId: number; event: ElementHistoryEvent }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();

  const [expanded, setExpanded] = useState(false);
  const run = useElementHistoryRun(elementId, expanded ? event.importRunId : null);

  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <li className={`${styles['row']} ${styles['rowImport']}`}>
      <button
        type="button"
        className={styles['importHead']}
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        <Chevron size={15} aria-hidden="true" className={styles['chevron']} />

        <span className={styles['importLabel']}>
          {t('graph.history.importRun', { run: event.importRunId })}
        </span>

        <span className={styles['importCount']}>
          {t('graph.history.changeCount', { count: event.linkCount })}
        </span>

        <Moment atUtc={event.atUtc} />
      </button>

      {expanded ? (
        run.isPending ? (
          <div aria-busy="true" className={styles['nested']}>
            <Skeleton height="1.75rem" announce />
            <Skeleton height="1.75rem" />
          </div>
        ) : run.isError ? (
          <p className={styles['nestedError']} role="alert">
            {describeError(run.error)}
          </p>
        ) : (
          <ol className={styles['nested']}>
            {run.data.items.map((link) => (
              <li key={link.id} className={styles['nestedRow']}>
                <SingleEvent event={link} inRun />
              </li>
            ))}
          </ol>
        )
      ) : null}
    </li>
  );
}

/**
 * Jedno ogniwo.
 *
 * ⚠ Zdanie składa **program**, nie tłumaczenie: nazwa pola i wartości są danymi, więc wchodzą
 * placeholderem (dec. 96). Dzięki temu nowe pole nie wymaga nowego klucza tłumaczenia.
 */
function SingleEvent({ event, inRun = false }: { event: ElementHistoryEvent; inRun?: boolean }) {
  const { t } = useTranslation();

  const Icon = event.kind ? ICONS[event.kind] : PencilLine;

  return (
    <>
      <Icon size={15} aria-hidden="true" className={styles['icon']} />

      <span className={styles['what']}>
        <span className={styles['kind']}>
          {t(`graph.history.kind.${event.kind ?? 'fieldChanged'}`)}
          {event.field ? (
            <span className={styles['field']}>
              {t(`graph.history.field.${event.field}`, { defaultValue: event.field })}
            </span>
          ) : null}
        </span>

        <Values event={event} />
      </span>

      {inRun ? null : (
        <span className={styles['who']}>
          {event.importRunId !== null ? (
            t('graph.history.byImport', { run: event.importRunId })
          ) : (
            <>
              {/* ⭐ Nazwisko człowieka, który odszedł, MUSI tu zostać: historia to zapis tego,
                  co się stało, a nie lista, z której się wybiera. */}
              <span className={accountStateClass(event.actorState, styles, 'whoOff')}>
                {event.actorDisplayName ?? t('graph.history.unknownActor')}
              </span>
              {accountStateKey(event.actorState)
                ? ` · ${t(accountStateKey(event.actorState)!)}`
                : ''}
            </>
          )}
        </span>
      )}

      <Moment atUtc={event.atUtc} />
    </>
  );
}

function Values({ event }: { event: ElementHistoryEvent }) {
  const { t } = useTranslation();

  if (event.relatedElementId !== null) {
    return (
      <span className={styles['values']}>
        {/* ⚠ Tytuł bywa pusty — historia przeżywa swój przedmiot (element zamknięty miękko). */}
        {event.relatedElementTitle ?? t('graph.history.goneElement', { id: event.relatedElementId })}
      </span>
    );
  }

  if (event.oldValue !== null && event.newValue !== null) {
    return (
      <span className={styles['values']}>
        <span className={styles['old']}>{event.oldValue}</span>
        {/* ⛔ Strzałka jako IKONA, nie znak czcionki — glify renderują się inaczej na każdym
            systemie i nie dają się pokolorować tokenem (spec 12B.7). */}
        <ArrowRight size={13} aria-hidden="true" className={styles['arrow']} />
        <span className={styles['new']}>{event.newValue}</span>
      </span>
    );
  }

  return event.newValue !== null ? (
    <span className={styles['values']}>
      <span className={styles['new']}>{event.newValue}</span>
    </span>
  ) : null;
}

/**
 * Chwila w czasie — **względnie**, z pełną datą w `title` i w `dateTime`.
 *
 * ⭐ „3 dni temu" odpowiada na pytanie, które człowiek naprawdę zadaje przy historii
 * („czy to świeże?"), a `Intl` odmienia to poprawnie po polsku (spec 12B.6).
 */
function Moment({ atUtc }: { atUtc: string }) {
  const { i18n } = useTranslation();

  const when = new Date(atUtc);
  const full = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(when);

  return (
    <time className={styles['when']} dateTime={atUtc} title={full}>
      {relative(when, i18n.language)}
    </time>
  );
}

/** Progi w sekundach → jednostka `Intl.RelativeTimeFormat`. Od najdrobniejszej. */
const STEPS: [seconds: number, unit: Intl.RelativeTimeFormatUnit][] = [
  [60, 'second'],
  [3600, 'minute'],
  [86_400, 'hour'],
  [604_800, 'day'],
  [2_629_800, 'week'],
  [31_557_600, 'month'],
  [Number.POSITIVE_INFINITY, 'year'],
];

const DIVISORS: Record<Intl.RelativeTimeFormatUnit, number> = {
  second: 1,
  seconds: 1,
  minute: 60,
  minutes: 60,
  hour: 3600,
  hours: 3600,
  day: 86_400,
  days: 86_400,
  week: 604_800,
  weeks: 604_800,
  month: 2_629_800,
  months: 2_629_800,
  quarter: 7_889_400,
  quarters: 7_889_400,
  year: 31_557_600,
  years: 31_557_600,
};

function relative(when: Date, language: string): string {
  const seconds = (when.getTime() - Date.now()) / 1000;
  const magnitude = Math.abs(seconds);

  const unit = STEPS.find(([limit]) => magnitude < limit)?.[1] ?? 'year';
  const value = Math.round(seconds / DIVISORS[unit]);

  return new Intl.RelativeTimeFormat(language, { numeric: 'auto' }).format(value, unit);
}
